import { createFeature, createReducer, on } from '@ngrx/store';
import { IChartDataEntry } from './interfaces/query-engine.interface';
import { QueryEngineActions } from './query-engine.actions';

export const queryEngineFeatureKey = 'queryEngine';

export interface State {
  chartData: Record<string, IChartDataEntry>;
  /**
   * Charts whose last request failed, by id. Without it "no data yet" is the only thing a chart
   * can see, so a failed query is indistinguishable from one still in flight and its loader never
   * stops.
   */
  failedCharts: Record<string, true>;
  httpResult: { action: string; error?: unknown };
}

export const initialState: State = {
  chartData: {},
  failedCharts: {},
  httpResult: { action: '' },
};

/** The map without one key, leaving it untouched when the key is not there. */
function without(map: Record<string, true>, key: string): Record<string, true> {
  if (!(key in map)) return map;
  const { [key]: _removed, ...rest } = map;
  return rest;
}

export const reducer = createReducer(
  initialState,
  on(QueryEngineActions.loadChartDataSuccess, (state, { chartId, data, isComparative }): State => {
    const key = String(chartId);
    const existing = state.chartData[key];
    const entry: IChartDataEntry = isComparative
      ? { chartId, data: existing?.data, comparative: data }
      : { chartId, data };
    return { ...state, chartData: { ...state.chartData, [key]: entry }, failedCharts: without(state.failedCharts, key) };
  }),
  on(QueryEngineActions.loadChartData, (state, { query }): State => ({
    ...state,
    // Asking again clears the previous verdict: the chart is in flight, not failed.
    failedCharts: without(state.failedCharts, String(query.id)),
  })),
  on(QueryEngineActions.loadChartDataFailure, (state, { chartId }): State => ({
    ...state,
    failedCharts: { ...state.failedCharts, [String(chartId)]: true },
  })),
  on(QueryEngineActions.unloadChartData, (state, { chartId }): State => {
    const { [String(chartId)]: _, ...rest } = state.chartData;
    return { ...state, chartData: rest, failedCharts: without(state.failedCharts, String(chartId)) };
  }),
  on(
    QueryEngineActions.httpResult,
    (state, { action, error }): State => ({ ...state, httpResult: { action, error } })
  )
);

export const queryEngineFeature = createFeature({
  name: queryEngineFeatureKey,
  reducer,
});
